import { CreditCard, Delete } from "@mui/icons-material";
import {
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";

import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { config } from "../App";
import Cart, { getTotalCartValue, generateCartItemsFrom } from "./Cart";
import "./Checkout.css";
import Footer from "./Footer";
import Header from "./Header";

const AddNewAddressView = ({
  authToken,
  newAddress,
  handleNewAddress,
  addAddress,
}) => {
  return (
    <Box display="flex" flexDirection="column">
      <TextField
        multiline
        minRows={4}
        placeholder="Enter your complete address"
        onChange={(e) =>
          handleNewAddress((newAddress) => ({
            ...newAddress,
            value: `${e.target.value}`,
          }))
        }
      />
      <Stack direction="row" my="1rem">
        <Button
          variant="contained"
          onClick={() => {
            addAddress(authToken, newAddress);
          }}
        >
          Add
        </Button>
        <Button
          variant="text"
          onClick={() =>
            handleNewAddress((currAddress) => ({
              ...currAddress,
              isAddingNewAddress: false,
            }))
          }
        >
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

const Checkout = () => {
  const authToken = localStorage.getItem("token");
  const [balance, setBalance] = useState(localStorage.getItem("balance"));
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [cartItems, setCartItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [userAddresses, setUserAddresses] = useState({ all: [], selected: "" });
  const [newUserAddress, setNewUserAddress] = useState({
    isAddingNewAddress: false,
    value: "",
  });

  useEffect(() => {
    const onLoad = async () => {
      await fetchUserAddresses(authToken);
    };
    onLoad();
  }, []);

  if (!authToken) {
    enqueueSnackbar("You must be logged in to access the checkout page", {
      variant: "info",
    });
    history.push("/login");
  }

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${config.endpoint}/products`);

      setProducts(response.data);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 500) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
        return null;
      } else {
        enqueueSnackbar(
          "Could not fetch products. Check that the backend is running, reachable, and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  };

  const fetchUserCart = async (authToken) => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${config.endpoint}/cart`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      return response.data;
    } catch {
      enqueueSnackbar(
        "Could not fetch cart details. Check that the backend is running, reachable, and returns valid JSON.",
        {
          variant: "error",
        }
      );
      return null;
    }
  };

  const fetchUserAddresses = async (authToken) => {
    if (!authToken) return;

    try {
      const response = await axios.get(`${config.endpoint}/user/addresses`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setUserAddresses({ ...userAddresses, all: response.data });
      return response.data;
    } catch {
      enqueueSnackbar(
        "Could not fetch addresses. Check that the backend is running, reachable, and returns valid JSON.",
        {
          variant: "error",
        }
      );
      return null;
    }
  };

  const addNewAddress = async (authToken, newUserAddress) => {
    try {
      await axios
        .post(
          `${config.endpoint}/user/addresses`,
          { address: newUserAddress.value },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setUserAddresses({ ...userAddresses, all: response.data });
          setNewUserAddress((currentNewUserAddress) => ({
            ...currentNewUserAddress,
            isAddingNewAddress: false,
            value: "",
          }));
          enqueueSnackbar("New Address added Successfully", {
            variant: "success",
          });
        });
    } catch (error) {
      if (error.response) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not add this address. Check that the backend is running, reachable, and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  };

  const deleteAddress = async (authToken, addressId) => {
    try {
      await axios
        .delete(`${config.endpoint}/user/addresses/${addressId}`, {
          headers: {
            Accept: "application/json, text/plain, /",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          enqueueSnackbar("Deleted", { variant: "success" });
          setUserAddresses({ ...userAddresses, all: response.data });
        });
    } catch (error) {
      if (error.response) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not delete this address. Check that the backend is running, reachable, and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  };

  const validateRequest = (cartItems, userAddresses) => {
    const userBalance = window.localStorage.getItem("balance");
    const cartTotal = getTotalCartValue(cartItems);

    if (userBalance === 0 || cartTotal > userBalance) {
      enqueueSnackbar("You do not have enough balance in your wallet for this purchase", { variant: "warning" });
      return false;
    }

    if (cartItems.length === 0) {
      enqueueSnackbar("Your cart is empty. Add items to your cart to proceed.", {
        variant: "warning",
      });
      return false;
    }

    if (userAddresses.all.length === 0) {
      enqueueSnackbar("Please add a new address before proceeding.", {
        variant: "warning",
      });
      return false;
    }

    if (!userAddresses.selected) {
      enqueueSnackbar("Please select one shipping address to proceed.", {
        variant: "warning",
      });
      return false;
    }

    return true;
  };

  const performCheckout = async (authToken, cartItems, userAddresses) => {
    const isValid = validateRequest(cartItems, userAddresses);

    if (isValid) {
      try {
        await axios
          .post(
            `${config.endpoint}/cart/checkout`,
            { addressId: userAddresses.selected },
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
              },
            }
          )
          .then((response) => {
            enqueueSnackbar("Checkout Successful", { variant: "success" });
            history.push("/thanks");
            return true;
          });
      } catch (error) {}
    }

    return false;
  };

  useEffect(() => {
    const onLoadHandler = async () => {
      const productsData = await fetchProducts();
      const userCartData = await fetchUserCart(authToken);

      if (productsData && userCartData) {
        const parsedCartItems = parseCartItems(userCartData, productsData);
        setCartItems(parsedCartItems);
      }
    };
    onLoadHandler();
  }, []);

  const parseCartItems = (userCartData, productsData) => {
    const cartItems = userCartData.map((cartItem) => {
      const product = productsData.find((p) => p._id === cartItem.productId);
      return {
        ...cartItem,
        name: product.name,
        category: product.category,
        cost: product.cost,
        rating: product.rating,
        image: product.image,
      };
    });
    return cartItems;
  };

  return (
    <>
      <Header />
      <Grid container>
        <Grid item xs={12} md={9}>
          <Box className="shipping-container" minHeight="100vh">
            <Typography color="#3C3C3C" variant="h4" my="1rem">
              Shipping
            </Typography>
            <Typography color="#3C3C3C" my="1rem">
              Manage all the shipping addresses you want. This way you won't
              have to enter the shipping address manually with every order.
              Select the address you want to get your order delivered.
            </Typography>
            <Divider />
            <Box>
              {userAddresses.all.length === 0 && (
                <Typography my="1rem">
                  No addresses found for this account. Please add one to proceed
                </Typography>
              )}
              {userAddresses.all.length > 0 &&
                userAddresses.all.map((address) => {
                  return (
                    <Box key={address._id} my="1rem">
                      <Typography
                        variant="body2"
                        className={
                          userAddresses.selected === address._id
                            ? "selected address-item"
                            : "not-selected address-item"
                        }
                        onClick={() => {
                          setUserAddresses({ ...userAddresses, selected: address._id });
                        }}
                        gutterBottom
                      >
                        {address.address}
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => {
                            deleteAddress(authToken, address._id);
                          }}
                        >
                          Delete
                        </Button>
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
            {newUserAddress.isAddingNewAddress === false && (
              <Button
                color="primary"
                variant="contained"
                id="add-new-btn"
                size="large"
                onClick={() => {
                  setNewUserAddress((currentNewUserAddress) => ({
                    ...currentNewUserAddress,
                    isAddingNewAddress: true,
                  }));
                }}
              >
                Add new address
              </Button>
            )}

            {newUserAddress.isAddingNewAddress === true && (
              <AddNewAddressView
                authToken={authToken}
                newAddress={newUserAddress}
                handleNewAddress={setNewUserAddress}
                addAddress={addNewAddress}
              />
            )}

            <Typography color="#3C3C3C" variant="h4" my="1rem">
              Payment
            </Typography>
            <Typography color="#3C3C3C" my="1rem">
              Payment Method
            </Typography>
            <Divider />

            <Box my="1rem">
              <Typography>Wallet</Typography>
              <Typography>
                Pay ${getTotalCartValue(cartItems)} of available ${balance}
              </Typography>
            </Box>

            <Button
              startIcon={<CreditCard />}
              variant="contained"
              onClick={async () => {
                await performCheckout(authToken, cartItems, userAddresses);
              }}
            >
              PLACE ORDER
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={3} bgcolor="#E9F5E1">
          <Cart isReadOnly products={products} items={cartItems} />
        </Grid>
      </Grid>
      <Footer />
    </>
  );
};

export default Checkout;



